import { httpClient } from '../httpClient';

// Spec 030 — Clustering Engine v2 API helpers.
// Backend blueprint: `modules/clustering_v2/routes.py`, prefix `/api/clustering`.
//
// Auth: `curate_purpose_proposals` for recluster; unrestricted read for runs/evidence.

export const clusteringV2Api = {
  /**
   * List engine runs for a data source.
   * @param {object} params
   * @param {number} params.data_source_id  Required.
   * @param {number} [params.limit]         Default 20.
   * @param {number} [params.offset]        Default 0.
   */
  listRuns: (params) =>
    httpClient.get('/clustering/runs', { params }),

  /**
   * Trigger a recluster for a data source.
   * Returns 202 with run object on success, 409 if already in-flight.
   * @param {object} body
   * @param {number} body.data_source_id  Required.
   */
  recluster: (body) =>
    httpClient.post('/clustering/recluster', body),

  /**
   * Get evidence rows for a run, with optional signal filter.
   * @param {number|string} runId
   * @param {object} [params]
   * @param {string} [params.signal]  FK | RARE_ID | COMMON_ID | VIEW | INDEX_PREFIX | NAME_PREFIX | SCHEMA
   */
  getRunEvidence: (runId, params) =>
    httpClient.get(`/clustering/runs/${runId}/evidence`, { params }),

  /**
   * Spec 052 US3 (FR-024) — evidence-**id**-keyed trace surface. Returns one
   * ClusterBindingEvidence row plus its full context: the proposal that carries
   * it (with engine provenance, template-match fields, and paginated
   * `member_evidence`), the EngineRun that produced it, and the register entry
   * the proposal's resolved purpose now feeds (`ropa_record_id` | null).
   * Backend: `GET /api/clustering/evidence/:evidenceId` (routes.py::get_evidence_by_id).
   * @param {number|string} evidenceId
   * @param {object} [params]
   * @param {number} [params.evidence_page]      Paginate proposal.member_evidence (default 1).
   * @param {number} [params.evidence_per_page]  Page size (default 200, max 1000).
   */
  getEvidenceById: (evidenceId, params) =>
    httpClient.get(`/clustering/evidence/${evidenceId}`, { params }),

  /**
   * Spec 052 FR-028 — force-reset a wedged 'running' EngineRun so the source
   * can be re-clustered. 200 on success, 409 when the run is not 'running'.
   * @param {number|string} runId
   */
  resetRun: (runId) =>
    httpClient.post(`/clustering/runs/${runId}/reset`),

  /**
   * List engine-version notifications (unacknowledged by default).
   * Permission: curate_purpose_proposals
   * @param {object} [params]
   * @param {boolean} [params.include_acknowledged]  Include acknowledged rows.
   */
  listVersionNotifications: (params) =>
    httpClient.get('/clustering/engine-versions/notifications', { params }),

  /**
   * Acknowledge a specific engine-version notification (FR-030-78).
   * @param {number|string} notificationId
   */
  acknowledgeVersionNotification: (notificationId) =>
    httpClient.post(`/clustering/engine-versions/notifications/${notificationId}/acknowledge`),

  // ── Industry Templates (T111) ──────────────────────────────────────────

  /** List canonical Saudi industry templates + workspace forks. */
  listIndustryTemplates: () =>
    httpClient.get('/industry-templates'),

  /**
   * Apply a canonical template — seeds starter purposes into the curation queue.
   * @param {string} industryKey  'banking' | 'healthcare' | 'retail' | 'government'
   */
  applyIndustryTemplate: (industryKey) =>
    httpClient.post(`/industry-templates/${industryKey}/apply`),

  /**
   * Fork a canonical template into an editable workspace copy.
   * @param {string} industryKey
   * @param {object} [body]
   * @param {string} [body.fork_name_en]
   * @param {string} [body.fork_name_ar]
   */
  forkIndustryTemplate: (industryKey, body = {}) =>
    httpClient.post(`/industry-templates/${industryKey}/fork`, body),

  /**
   * Edit a workspace fork (name and/or purposes).
   * @param {number} forkId
   * @param {object} patches  { name_en?, name_ar?, purposes? }
   */
  editIndustryTemplateFork: (forkId, patches) =>
    httpClient.patch(`/industry-templates/forks/${forkId}`, patches),

  /**
   * Delete a workspace fork.
   * @param {number} forkId
   */
  deleteIndustryTemplateFork: (forkId) =>
    httpClient.delete(`/industry-templates/forks/${forkId}`),

  /**
   * Sync upstream changes from the canonical template into a fork.
   * @param {number} forkId
   * @param {object} body  { strategy: 'merge_all'|'replace_wholesale'|'ignore', selected_changes?: string[] }
   */
  syncUpstreamFork: (forkId, body) =>
    httpClient.post(`/industry-templates/forks/${forkId}/sync-upstream`, body),

  // ── Engine config overrides (T154/T155) ────────────────────────────────────
  getConfig: () => httpClient.get('/clustering/config'),
  setConfigOverride: (key, value, reason) =>
    httpClient.post('/clustering/config/override', { key, value, reason }),
  deleteConfigOverride: (key) =>
    httpClient.delete(`/clustering/config/override/${key}`),

  // ── KPI metrics (T169) ────────────────────────────────────────────────────
  getKpis: () => httpClient.get('/clustering/kpis'),
};
